import React, { useState, useContext, useRef } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AnimatedBackground from "@/components/AnimatedBackground";
import "./Home.css";

const Home = () => {
  const [text, setText] = useState("");
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listening, setListening] = useState(false);
const recognitionRef = useRef(null);
const finalTranscriptRef = useRef('');
const lastFinalRef = useRef('');
const listeningRef = useRef(false);



useEffect(() => {
  return () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };
}, []);



  const handleCheckGrammar = () => {
    if (!user) {
      const encodedText = encodeURIComponent(text);
      navigate(`/signup?redirect=/checker&text=${encodedText}`);
    } else {
      navigate("/checker", { state: { text } });
    }
  };
  const toggleVoiceInput = () => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    alert("Voice input not supported in this browser");
    return;
  }

 // 🛑 Stop listening
if (listening && recognitionRef.current) {
  recognitionRef.current.stop();
  recognitionRef.current = null;
  setListening(false);
  listeningRef.current = false; // ✅ CRITICAL
  finalTranscriptRef.current = '';
  lastFinalRef.current = '';
  return;
}



  // ▶️ Start listening
  const recognition = new SpeechRecognition();
  recognition.lang = "en-IN";
  recognition.continuous = true;
  recognition.interimResults = true;

  finalTranscriptRef.current = '';
recognitionRef.current = recognition;
setListening(true);
listeningRef.current = true; // ✅ CRITICAL


 recognition.onresult = (event) => {
  let interim = '';
  let finalText = finalTranscriptRef.current;

  for (let i = event.resultIndex; i < event.results.length; i++) {
    const chunk = event.results[i][0].transcript.trim();

    if (event.results[i].isFinal) {
      if (chunk !== lastFinalRef.current) {
        finalText += chunk + ' ';
        lastFinalRef.current = chunk;
      }
    } else {
      interim += chunk + ' ';
    }
  }

  // ✅ OUTSIDE loop (this is the fix)
  finalTranscriptRef.current = finalText;
  setText((finalText + interim).trim());
};


  recognition.onerror = () => {
    setListening(false);
    recognitionRef.current = null;
  };

 recognition.onend = () => {
  if (listeningRef.current) {
    setTimeout(() => {
      try {
        recognition.start();
      } catch {}
    }, 300);
  }
};

  recognition.start();
};


  return (
    <div className="home-container">

      {/* SEO */}
      <Helmet>
        <title>GrammAA - Free AI Grammar Checker, Sentence Corrector & Rewriter</title>

        <meta
          name="description"
          content="GrammAA is the most advanced AI grammar checker powered by GPT-5.1. Fix grammar errors instantly, rewrite sentences, improve clarity, tone, and writing quality."
        />

        <meta
          name="keywords"
          content="
            AI grammar checker, free grammar checker, GPT grammar, sentence rewriter,
            grammar correction tool, grammar checker 2025, rewrite text AI,
            paraphrasing tool, fix spelling errors, English grammar corrector,
            punctuation checker, improve writing, AI writing assistant,
            essay corrector, professional grammar check"
        />

        <meta name="author" content="aiwebAA.com" />

        {/* OG */}
        <meta property="og:title" content="GrammAA — AI Grammar Checker & Sentence Rewriter" />
        <meta
          property="og:description"
          content="Fix grammar mistakes instantly using advanced AI. Rewrite text, improve clarity, tone, and writing quality with GPT-5.1 technology."
        />
        <meta property="og:image" content="/icons/logo-512.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://grammaa.com" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GrammAA — AI Grammar Checker" />
        <meta name="twitter:description" content="Instant grammar correction & sentence rewriting powered by GPT-5.1." />
        <meta name="twitter:image" content="/icons/logo-512.png" />

        {/* Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "GrammAA",
              "url": "https://grammaa.com",
              "description": "Advanced AI grammar checking & sentence rewriting tool powered by GPT-5.1.",
              "applicationCategory": "WritingAssistant",
              "creator": {
                "@type": "Organization",
                "name": "aiwebAA.com",
                "url": "https://aiwebAA.com"
              }
            }
          `}
        </script>
      </Helmet>

      <div className="glass-background"></div>
      <AnimatedBackground />

      {/* NAV */}
      <nav className="home-nav fade-in">
        <div className="nav-brand" onClick={() => navigate("/")}>
          <div className="nav-logo-wrap">
            <img src="/icons/logo-512.png" alt="GrammAA Logo" className="nav-logo" />
          </div>
          <span className="brand-text">GrammAA</span>
        </div>

        <div className="nav-links">
          {user ? (
            <>
              <Button variant="ghost" onClick={() => navigate("/dashboard")}>Dashboard</Button>
              <Button variant="ghost" onClick={() => navigate("/pricing")}>Pricing</Button>
              <Button onClick={() => navigate("/checker")}>Checker</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/pricing")}>Pricing</Button>
              <Button variant="ghost" onClick={() => navigate("/login")}>Login</Button>
              <Button onClick={() => navigate("/signup")}>Sign Up</Button>
            </>
          )}
        </div>
      </nav>

      {/* HERO */}
      <div className="home-content fade-in">
        <div className="hero-section">
          <h1 className="hero-title">
            AI Grammar Checker
            <span className="hero-subtitle">GrammAA</span>
          </h1>

          <p className="hero-description">
            Perfect your writing with advanced AI grammar checking.
            Get instant corrections, explanations, readability fixes, and smart rewriting.
          </p>
        </div>

        {/* CHECKER BOX */}
        <div className="checker-section glass-panel glow-effect">
          <Textarea
  placeholder="Type or paste your text here..."
  value={text}
  onChange={(e) => setText(e.target.value)}
  className="glass-textarea"
/>

<Button
  onClick={toggleVoiceInput}
  className={`glass-btn voice-btn ${listening ? 'listening' : ''}`}
  size="sm"
>
  {listening ? (
    <>
      <span className="voice-icon animate-pulse">🔴</span> Listening (Click to stop)
    </>
  ) : (
    <>
      <span className="voice-icon">🎤</span> Speak Now
    </>
  )}
</Button>


          <Button
            onClick={handleCheckGrammar}
            disabled={!text.trim()}
            className="glass-btn check-btn"
            size="lg"
          >
            Check Grammar
          </Button>
        </div>

        {/* FEATURES */}
        <div className="features-grid">
          <div className="feature-card glass-panel">
            <div className="feature-icon">🔥</div>
            <h3>Advanced AI</h3>
            <p>Powered by GPT-5.1 for extremely accurate grammar correction.</p>
          </div>

          <div className="feature-card glass-panel">
            <div className="feature-icon">⚡</div>
            <h3>Instant Results</h3>
            <p>Fix grammar and rewrite sentences in seconds.</p>
          </div>

          <div className="feature-card glass-panel">
            <div className="feature-icon">✨</div>
            <h3>Smart Rewriting</h3>
            <p>Choose professional, academic, creative, or simple rewrite styles.</p>
          </div>
        </div>

        {/* WHY CHOOSE US */}
        <div className="why-section fade-in">
          <h2 className="section-title">Why Choose <span className="gradient-text">GrammAA?</span></h2>

          <div className="why-grid">
            <div className="why-card glass-panel">
              <div className="why-icon">🤖</div>
              <h3>GPT-5.1 Accuracy</h3>
              <p>Next-gen grammar intelligence.</p>
            </div>

            <div className="why-card glass-panel">
              <div className="why-icon">🔒</div>
              <h3>Fully Private</h3>
              <p>Your text is encrypted & never shared.</p>
            </div>

            <div className="why-card glass-panel">
              <div className="why-icon">💡</div>
              <h3>Smart Suggestions</h3>
              <p>Fix clarity, tone, flow, and structure.</p>
            </div>

            <div className="why-card glass-panel">
              <div className="why-icon">⚙️</div>
              <h3>Continuous Updates</h3>
              <p>Improving weekly for perfection.</p>
            </div>
          </div>
        </div>

        {/* HOW IT WORKS */}
        <div className="how-section fade-in">
          <h2 className="section-title">How It <span className="gradient-text">Works</span></h2>

          <div className="how-steps">
            <div className="step-card glass-panel">
              <span className="step-number">1</span>
              <h3>Enter Your Text</h3>
              <p>Paste your writing.</p>
            </div>

            <div className="step-card glass-panel">
              <span className="step-number">2</span>
              <h3>Click Check</h3>
              <p>AI analyzes grammar, tone, clarity.</p>
            </div>

            <div className="step-card glass-panel">
              <span className="step-number">3</span>
              <h3>Get Instant Fixes</h3>
              <p>Corrected text + rewrites instantly.</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="faq-section glass-panel fade-in">
          <h2 className="section-title">Frequently Asked Questions</h2>

          <div className="faq-list">

            <div className="faq-item">
              <h3>Is GrammAA free?</h3>
              <p>Yes. You get 3 free grammar checks per day. Upgrade to Premium for unlimited checks and advanced rewriting tools.</p>
            </div>

            <div className="faq-item">
              <h3>What AI model does GrammAA use?</h3>
              <p>GrammAA runs on GPT-5.1 - one of the most advanced grammar and writing models available in 2025.</p>
            </div>

            <div className="faq-item">
              <h3>Is my data safe?</h3>
              <p>Yes. All text is encrypted, never stored permanently, and never shared. GrammAA is built with a strict privacy-first policy.</p>
            </div>

            <div className="faq-item">
              <h3>Can I use GrammAA for essays?</h3>
              <p>Absolutely. GrammAA improves clarity, tone, grammar, spelling, structure, and readability — perfect for assignments and essays.</p>
            </div>

            <div className="faq-item">
              <h3>Does GrammAA rewrite sentences?</h3>
              <p>Yes. Rewrite styles include professional, academic, creative, simple, formal, friendly, and more.</p>
            </div>

            <div className="faq-item">
              <h3>Can GrammAA improve tone?</h3>
              <p>Yes. It automatically adjusts tone, clarity, structure, and readability.</p>
            </div>

            <div className="faq-item">
              <h3>Is it better than Grammarly?</h3>
              <p>GrammAA uses GPT-5.1, offering deeper context understanding and more natural corrections.</p>
            </div>

            <div className="faq-item">
              <h3>Does it support long documents?</h3>
              <p>Yes - unlimited for Premium users.</p>
            </div>

            <div className="faq-item">
              <h3>Can I use it on mobile?</h3>
              <p>Yes, GrammAA is fully responsive on mobile, tablet, and desktop.</p>
            </div>

            <div className="faq-item">
              <h3>Does it detect plagiarism?</h3>
              <p>Plagiarism checker is coming soon for Premium users.</p>
            </div>

            <div className="faq-item">
              <h3>What languages are supported?</h3>
              <p>English currently. Multilingual support is coming soon.</p>
            </div>

            <div className="faq-item">
              <h3>Who built GrammAA?</h3>
              <p>Built by <strong>aiwebAA.com</strong>, an AI-powered web development agency.</p>
            </div>

          </div>
        </div>

        {/* ABOUT */}
        <div className="about-section fade-in">
          <h2 className="section-title">
            About <span className="gradient-text">GrammAA</span>
          </h2>

          <p className="about-text">
            GrammAA helps students, writers, professionals, and businesses produce clean, professional writing instantly using advanced AI.
          </p>

          <p className="built-by">
            Built with ❤️ by <a href="https://aiwebAA.com" target="_blank">aiwebAA.com</a>
          </p>
        </div>

        {/* FOOTER */}
<footer className="footer-section">
  <div className="footer-brand">
    <img src="/icons/logo-512.png" className="nav-logo footer-logo" />
    <span className="footer-title">GrammAA</span>
  </div>

  <div className="footer-links">
    <a onClick={() => navigate("/pricing")}>Pricing</a>
    <a onClick={() => navigate("/checker")}>Checker</a>
    <a onClick={() => navigate("/history")}>History</a>
    <a onClick={() => navigate("/dashboard")}>Dashboard</a>

    {/* ⭐ New Legal Links */}
    <a onClick={() => navigate("/privacy")}>Privacy Policy</a>
    <a onClick={() => navigate("/terms")}>Terms & Conditions</a>
    <a onClick={() => navigate("/refund")}>Refund Policy</a>
    <a onClick={() => navigate("/shipping")}>Shipping Policy</a>
    <a onClick={() => navigate("/contact")}>Contact Us</a>
  </div>

  <p className="footer-copy">© {new Date().getFullYear()} GrammAA. All rights reserved.</p>
</footer>


      </div>
    </div>
  );
};

export default Home;
